import { z } from 'zod';
import type {
  ChatMessage,
  EndReason,
  GameEvent,
  GameId,
  MatchId,
  UserId,
} from './types';

/** Identité minimale d'un joueur, telle qu'affichée à son adversaire. */
export interface PlayerCard {
  id: UserId;
  username: string;
  displayName: string;
  rating: number;
  /** Adversaire artificiel : toujours connecté, hors classement. */
  isBot: boolean;
}

export interface QueueStatus {
  gameId: GameId;
  /** Nombre de joueurs dans la file. */
  size: number;
  /** Position du joueur dans la file, 1 = prochain servi ; `null` s'il n'y est pas. */
  position: number | null;
}

export interface MatchFound {
  matchId: MatchId;
  gameId: GameId;
  opponent: PlayerCard;
}

/** Instantané complet d'une partie, envoyé à la connexion et après chaque action. */
export interface MatchSnapshot {
  matchId: MatchId;
  gameId: GameId;
  you: PlayerCard;
  opponent: PlayerCard;
  /** Projection de l'état propre au destinataire (`BattleshipView` pour la bataille navale). */
  view: unknown;
  log: GameEvent[];
  chat: ChatMessage[];
  opponentOnline: boolean;
  /** Horodatage serveur, pour caler le décompte du chronomètre côté client. */
  serverTime: number;
}

export interface MatchEnded {
  matchId: MatchId;
  winnerId: UserId | null;
  reason: EndReason;
  ratings: Array<{ userId: UserId; before: number; after: number }>;
}

export interface PresenceUpdate {
  matchId: MatchId;
  userId: UserId;
  online: boolean;
}

export type AckResult<T = undefined> =
  | { ok: true; data: T }
  | { ok: false; error: string };

export type Ack<T = undefined> = (result: AckResult<T>) => void;

/** Évènements émis par le serveur vers le navigateur. */
export interface ServerToClientEvents {
  'queue:status': (payload: QueueStatus) => void;
  'match:found': (payload: MatchFound) => void;
  'match:state': (payload: MatchSnapshot) => void;
  'match:event': (payload: { matchId: MatchId; events: GameEvent[] }) => void;
  'match:ended': (payload: MatchEnded) => void;
  'chat:message': (payload: ChatMessage) => void;
  'presence': (payload: PresenceUpdate) => void;
  'notice': (payload: { level: 'info' | 'error'; message: string }) => void;
  'session:invalid': () => void;
}

/** Évènements émis par le navigateur vers le serveur. */
export interface ClientToServerEvents {
  'queue:join': (payload: { gameId: GameId }, ack: Ack<QueueStatus>) => void;
  'match:solo': (
    payload: { gameId: GameId; level?: string; side?: string },
    ack: Ack<{ matchId: MatchId }>,
  ) => void;
  'queue:leave': (payload: Record<string, never>, ack: Ack) => void;
  'match:sync': (payload: { matchId: MatchId }, ack: Ack<MatchSnapshot>) => void;
  'match:action': (payload: { matchId: MatchId; action: unknown }, ack: Ack) => void;
  'chat:send': (payload: { matchId: MatchId; body: string }, ack: Ack) => void;
}

export const CHAT_MAX_LENGTH = 500;

export const queueJoinSchema = z.object({ gameId: z.string().min(1).max(50) });
export const soloStartSchema = z.object({
  gameId: z.string().min(1).max(50),
  level: z.string().max(30).optional(),
  side: z.string().max(30).optional(),
});
export const matchRefSchema = z.object({ matchId: z.string().uuid() });
export const matchActionSchema = z.object({
  matchId: z.string().uuid(),
  action: z.unknown(),
});
export const chatSendSchema = z.object({
  matchId: z.string().uuid(),
  body: z.string().trim().min(1).max(CHAT_MAX_LENGTH),
});
